import { useState } from "react";
import { api, formatApiErrorDetail } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { Section, Field, NumField, toMaybeNumber, fmtUsd } from "./shared";

const TIERS = ["none", "starter", "pro", "scale", "whale", "titan"];
const VERTICALS = [
    { key: "whiteHat", label: "White-hat" },
    { key: "grayHat", label: "Gray-hat" },
    { key: "casino", label: "Casino & Gambling" },
];
const MIN_TOPUPS = [
    ["meta_standard", "Meta Standard"], ["meta_flex", "Meta Flex"], ["google", "Google"], ["tiktok", "TikTok"],
    ["snapchat", "Snapchat"], ["bing", "Bing"], ["taboola", "Taboola"], ["outbrain", "Outbrain"],
];

export function UserPricingForm({ user, policy, onSaved }) {
    const [form, setForm] = useState({
        tier_override: user.tier_override || "none",
        ad_request_fee_percent_override: user.ad_request_fee_percent_override ?? "",
        fee_override_whiteHat: user.fee_override_whiteHat ?? "",
        fee_override_grayHat: user.fee_override_grayHat ?? "",
        fee_override_casino: user.fee_override_casino ?? "",
        ...Object.fromEntries(MIN_TOPUPS.map(([k]) => [`min_topup_override_${k}`, user[`min_topup_override_${k}`] ?? ""])),
    });
    const [saving, setSaving] = useState(false);
    const setNum = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { tier_override: form.tier_override };
            Object.entries(form).forEach(([k, v]) => { if (k !== "tier_override") payload[k] = toMaybeNumber(v); });
            await api.patch(`/admin/users/${user.id}`, payload);
            toast.success("Fiyatlandırma güncellendi");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="user-pricing-form">
            <Section title="Uygulanan komisyon (canlı)" subtitle={`Kademe: ${policy.tier} · Aylık harcama ${fmtUsd(policy.monthly_spend)} · Öncelik: dikey override → genel override → kademe tablosu`} testid="user-effective-policy">
                <div className="grid grid-cols-3 gap-3">
                    {VERTICALS.map((v) => {
                        const eff = policy.fee_by_vertical?.[v.key];
                        const table = policy.tier_table?.[v.key];
                        const overridden = eff !== table;
                        return (
                            <div key={v.key} data-testid={`effective-fee-${v.key}`} className={`rounded-xl border p-3 ${overridden ? "border-primary/40 bg-primary/5" : "border-white/10 bg-background/40"}`}>
                                <p className="text-[11px] text-muted-foreground">{v.label}</p>
                                <p className="font-display font-bold text-2xl mt-0.5">{eff}%</p>
                                <p className="text-[11px] text-muted-foreground">{overridden ? `özel · tablo ${table}%` : "kademe tablosu"}</p>
                            </div>
                        );
                    })}
                </div>
            </Section>

            <Section title="Kullanıcıya özel komisyon" subtitle="Boş bırakılan alanlarda hacim kademesi tablosu geçerlidir. Dikey bazlı alan genel override'dan önceliklidir." testid="user-fee-overrides">
                <div className="grid md:grid-cols-4 gap-4">
                    <Field label="Kademe override">
                        <select value={form.tier_override} onChange={(e) => setForm((f) => ({ ...f, tier_override: e.target.value }))} data-testid="pricing-tier" className="w-full bg-background border border-white/10 rounded-lg px-3 h-9 text-sm">
                            {TIERS.map((t) => <option key={t} value={t}>{t === "none" ? "otomatik (harcamaya göre)" : t}</option>)}
                        </select>
                    </Field>
                    {VERTICALS.map((v) => (
                        <NumField key={v.key} label={`${v.label} fee %`} value={form[`fee_override_${v.key}`]} onChange={setNum(`fee_override_${v.key}`)} testid={`pricing-fee-${v.key}`} placeholder={`boş = ${policy.tier_table?.[v.key]}%`} />
                    ))}
                </div>
                <NumField label="Genel fee override % (tüm dikeyler — eski alan)" value={form.ad_request_fee_percent_override} onChange={setNum("ad_request_fee_percent_override")} testid="pricing-fee-general" hint="Dikey bazlı alanlar boşsa bu değer tüm dikeylere uygulanır." />
            </Section>

            <Section title="Minimum ilk yükleme (platform bazlı)" subtitle="Boş = Site İçeriği'ndeki global minimum." testid="user-min-topups">
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {MIN_TOPUPS.map(([k, label]) => (
                        <NumField key={k} label={label} value={form[`min_topup_override_${k}`]} onChange={setNum(`min_topup_override_${k}`)} testid={`pricing-min-${k}`} placeholder={`boş = $${policy.min_topups?.[k] ?? "—"}`} />
                    ))}
                </div>
                <div className="flex justify-end">
                    <Button type="submit" disabled={saving} data-testid="pricing-save" className="bg-primary text-primary-foreground">{saving ? "Kaydediliyor…" : "Fiyatlandırmayı kaydet"}</Button>
                </div>
            </Section>
        </form>
    );
}
